// Unified farm-list control shared by every farm picker in the panel — the Farms
// landing list (farms-view.ts), the Clicker list (clicker-view.ts) and the Remote
// ADB list (adb-proxy-view.ts). Two pieces:
//
//   1. `makeFarmRow` — builds one `.clicker-farm-row` from a FarmApiRecord with the
//      SAME badge set everywhere (flag, kind/AGG, type/VMOS, image version, team),
//      so the three lists can never drift again (previously only Farms showed the
//      version). The caller supplies only the per-list bits: live online state, the
//      meta text and the click handler.
//   2. A shared filter bar (`buildFarmFilter` / `applyFarmFilter`) with a name
//      search plus Status, Type and — for superadmins — Teams as multi-select
//      drop-downs (`filter-select.ts`), so the bar stays one line. Filtering
//      is purely client-side off each row's stamped `data-*` attributes, so it works
//      identically for all three lists without holding onto the farms array.
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { FarmType } from './farm-type';
import { FarmKind, isAggregatorFarm } from './farm-kind';
import { farmRowInner } from './farm-row';
import { teamFilterMatches } from './team-filter';
import { filterSelect, pickFilterSelect, wireFilterSelects } from './filter-select';
import { fetchTeams } from '../users/teams-shared';
/**
 * Category for the type filter (one per farm). Aggregator topology wins over the
 * self-reported type (an aggregator has no image, so 'vmos' vs 'regular' is moot);
 * otherwise VMOS deployments are 'vmos' and everything else 'regular'.
 */
function farmCategory(f) {
    if (isAggregatorFarm(f.farm_kind))
        return 'aggregator';
    return (f.farm_type ?? FarmType.Regular) === FarmType.Vmos ? 'vmos' : 'regular';
}
/**
 * The full, consistent badge-field set for one farm — used by every list so the
 * flag/kind/type/version badges render identically. The version badge tracks the
 * docker image vs Docker Hub, which is meaningless for an aggregator farm (no
 * image), so it's suppressed there.
 */
function farmBadgeFields(f) {
    const agg = isAggregatorFarm(f.farm_kind);
    return {
        country: f.location,
        farmType: f.farm_type ?? FarmType.Regular,
        farmKind: f.farm_kind ?? FarmKind.Host,
        version: agg ? null : (f.farm_version ?? null),
        latestVersion: f.latest_version ?? null,
        updateAvailable: agg ? false : (f.update_available ?? false),
    };
}
/**
 * Build one farm row element with the unified badge set, and stamp the `data-*`
 * attributes the filter reads. The caller passes the per-list bits: the resolved
 * live `online` state, the right-aligned `meta` text, an optional `lastSeen`
 * timestamp (Farms only) and the click handler.
 */
/**
 * Stamp the `data-*` attributes {@link applyFarmFilter} reads onto any element that
 * represents one farm. Split out of {@link makeFarmRow} so a surface that lists farms
 * in its own shape — the My Devices Config accordion — can drive the SAME filter bar
 * instead of growing a parallel one.
 */
export function stampFarmFilterData(el, f, online) {
    const ds = el.dataset;
    ds['teamId'] = f.team_id != null ? String(f.team_id) : '';
    ds['online'] = online ? '1' : '0';
    ds['category'] = farmCategory(f);
    ds['name'] = f.name.toLowerCase();
}
export function makeFarmRow(f, opts) {
    const badges = farmBadgeFields(f);
    const row = document.createElement('div');
    row.className = 'clicker-farm-row';
    row.innerHTML = farmRowInner({
        online: opts.online,
        name: f.name,
        teamName: f.team_name,
        meta: opts.meta,
        lastSeen: opts.lastSeen ?? null,
        ...badges,
    });
    stampFarmFilterData(row, f, opts.online);
    // So a poll-driven update can find the row and patch its live bits in place.
    row.dataset['farmId'] = String(f.id);
    row.addEventListener('click', opts.onClick);
    return row;
}
export function createFarmFilterState() {
    return { name: '', online: 'all', types: new Set(), teams: new Set() };
}
const TYPE_OPTIONS = [
    { key: 'regular', label: t('nav.farm_category_regular') },
    { key: 'vmos', label: 'VMOS' },
    { key: 'aggregator', label: 'AGG' },
];
/** True when the row (via its stamped dataset) passes the current filter. */
function rowMatches(state, ds) {
    if (state.name && !(ds['name'] ?? '').includes(state.name))
        return false;
    if (state.online === 'online' && ds['online'] !== '1')
        return false;
    if (state.online === 'offline' && ds['online'] !== '0')
        return false;
    if (state.types.size && !state.types.has((ds['category'] ?? '')))
        return false;
    const teamId = ds['teamId'] ? Number.parseInt(ds['teamId'], 10) : null;
    if (!teamFilterMatches(state.teams, teamId))
        return false;
    return true;
}
/**
 * Show/hide the already-rendered rows of `list` per the current filter state.
 * `rowSelector` names the elements carrying the stamped data (see
 * {@link stampFarmFilterData}); it defaults to the shared row, and the Config
 * accordion passes its own.
 */
export function applyFarmFilter(list, state, rowSelector = '.clicker-farm-row') {
    list.querySelectorAll(rowSelector).forEach((row) => {
        row.style.display = rowMatches(state, row.dataset) ? '' : 'none';
    });
}
/**
 * Render the filter bar into `el` and wire it.
 * `showTeams` gates the (superadmin-only) Teams drop-down. `opts.trailing`, when given,
 * is appended at the far right of the bar (e.g. the Clicker page's Settings button,
 * so it shares the filter row instead of needing its own toolbar above). `onChange`
 * re-applies the filter to the list; called after every control change.
 */
export async function buildFarmFilter(el, state, _farms, opts, onChange) {
    let teams = [];
    if (opts.showTeams) {
        try {
            teams = await fetchTeams();
        }
        catch { /* no Teams facet, bar still works */ }
        for (const id of [...state.teams]) {
            if (!teams.some((t) => t.id === id))
                state.teams.delete(id);
        }
    }
    // Status holds one value, so its drop-down is single-select and "no pick" IS `all` —
    // that is why there is no All option any more, the way the other facets show All when
    // nothing is ticked.
    const teamOptions = opts.showTeams ? teams.map((tm) => ({ key: String(tm.id), label: tm.name, on: state.teams.has(tm.id) })) : [];
    el.innerHTML = `
        <input type="text" class="farm-filter__search" placeholder="${t('nav.search_farms')}" value="${esc(state.name)}" />
        ${filterSelect(t('common.status'), 'online', [
        { key: 'online', label: t('common.online'), on: state.online === 'online' },
        { key: 'offline', label: t('common.offline'), on: state.online === 'offline' },
    ], { single: true })}
        ${filterSelect(t('nav.filter_type'), 'type', TYPE_OPTIONS.map((c) => ({ key: c.key, label: c.label, on: state.types.has(c.key) })))}
        ${filterSelect(t('nav.teams'), 'teams', teamOptions)}
    `;
    if (opts.trailing)
        el.appendChild(opts.trailing);
    const search = el.querySelector('.farm-filter__search');
    search.addEventListener('input', () => { state.name = search.value.trim().toLowerCase(); onChange(); });
    // The bar is rebuilt into a fresh element on each render, but guard anyway: a second
    // delegated handler on the same element would apply every pick twice.
    if (el.dataset['wired'])
        return;
    el.dataset['wired'] = '1';
    wireFilterSelects(el);
    el.addEventListener('click', (e) => {
        const opt = e.target.closest('.fsel__opt');
        if (!opt || !el.contains(opt))
            return;
        const key = opt.dataset['key'] ?? '';
        const role = opt.closest('.fsel').dataset['role'];
        const on = pickFilterSelect(opt);
        const put = (set, value) => { if (on)
            set.add(value);
        else
            set.delete(value); };
        if (role === 'online')
            state.online = on ? key : 'all';
        else if (role === 'type')
            put(state.types, key);
        else if (role === 'teams')
            put(state.teams, Number.parseInt(key, 10));
        onChange();
    });
}
